import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Plus, Edit2, Trash2, Target, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import { sortKPIs } from '../sortUtils';

const FREQ = ['monthly', 'quarterly', 'annually'];

const emptyKpi = () => ({ name: '', description: '', target_value: 0, unit: '', frequency: 'quarterly', owner_member_id: '', is_active: true });

export default function KPIsTab({ api, isAdmin, members }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [reading, setReading] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyKpi());
  const [readingForm, setReadingForm] = useState({ period: '', value: 0, notes: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get('/board/kpis'); setList(data); } catch (e) { /* noop */ }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const memberName = (id) => members.find(m => m.id === id)?.name || '—';
  const openCreate = () => { setEditing(null); setForm(emptyKpi()); setOpen(true); };
  const openEdit = (k) => { setEditing(k); setForm({ ...emptyKpi(), ...k }); setOpen(true); };
  const openReading = (k) => { setReading(k); setReadingForm({ period: '', value: 0, notes: '' }); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    setBusy(true);
    try {
      if (editing) await api.put(`/board/kpis/${editing.id}`, form);
      else await api.post('/board/kpis', form);
      toast.success('Saved'); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };
  const del = async (k) => {
    if (!window.confirm(`Delete KPI "${k.name}"?`)) return;
    try { await api.delete(`/board/kpis/${k.id}`); toast.success('Deleted'); load(); } catch (e) { toast.error('Failed'); }
  };
  const saveReading = async () => {
    if (!readingForm.period.trim()) { toast.error('Period required (e.g. 2026-Q1)'); return; }
    setBusy(true);
    try {
      await api.post(`/board/kpis/${reading.id}/readings`, readingForm);
      toast.success('Reading added'); setReading(null); load();
    } catch (e) { toast.error('Failed'); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="kpis-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#2D1B4E]">Strategic KPIs</h2>
        {isAdmin && <Button onClick={openCreate} className="bg-[#6B3FA0]" data-testid="add-kpi-btn"><Plus className="h-4 w-4 mr-1" /> Add KPI</Button>}
      </div>

      {list.length === 0 ? (
        <p className="text-center py-12 text-[#8E85A0] text-sm">No KPIs defined.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {sortKPIs(list).map(k => {
            const readings = k.readings || [];
            const latest = readings[readings.length - 1];
            const prev = readings[readings.length - 2];
            const pct = k.target_value > 0 && latest ? Math.min(100, (latest.value / k.target_value) * 100) : 0;
            const trending = latest && prev ? (latest.value > prev.value ? 'up' : latest.value < prev.value ? 'down' : 'flat') : null;
            return (
              <Card key={k.id} className="border-[#DED8EB] bg-white" data-testid={`kpi-${k.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-bold text-[#2D1B4E]"><Target className="h-4 w-4 inline mr-1 text-[#6B3FA0]" />{k.name}</p>
                      <p className="text-[10px] text-[#8E85A0] uppercase tracking-wider mt-0.5">{k.frequency} · Owner: {k.owner_member_id ? memberName(k.owner_member_id) : '—'}</p>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openReading(k)} className="h-7 w-7 text-[#43A047]" title="Add reading" data-testid={`add-reading-${k.id}`}><Plus className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(k)} className="h-7 w-7"><Edit2 className="h-3 w-3" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => del(k)} className="h-7 w-7 text-[#D9534F]"><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    )}
                  </div>
                  {k.description && <p className="text-xs italic text-[#5A4D6E] mt-1">{k.description}</p>}
                  <div className="mt-3 flex items-baseline gap-2">
                    <span className="text-3xl font-bold font-mono text-[#6B3FA0]" data-testid={`kpi-value-${k.id}`}>
                      {latest ? `${k.unit === '$' ? '$' : ''}${latest.value}${k.unit && k.unit !== '$' ? k.unit : ''}` : '—'}
                    </span>
                    <span className="text-xs text-[#8E85A0]">/ target {k.unit === '$' ? '$' : ''}{k.target_value || '—'}{k.unit && k.unit !== '$' ? k.unit : ''}</span>
                    {trending === 'up' && <TrendingUp className="h-4 w-4 text-[#43A047]" />}
                    {trending === 'down' && <TrendingDown className="h-4 w-4 text-[#D9534F]" />}
                  </div>
                  {k.target_value > 0 && latest && (
                    <div className="mt-2 h-2 bg-[#EDE7F6] rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: pct >= 100 ? '#43A047' : pct >= 75 ? '#FFB74D' : '#9B7ED8' }} />
                    </div>
                  )}
                  {readings.length > 0 && (
                    <div className="mt-2 text-[10px] text-[#8E85A0] flex flex-wrap gap-1.5">
                      {readings.slice(-6).map((r, i) => (
                        <span key={i} className="bg-[#F0EDF6] rounded px-1.5 py-0.5 font-mono">
                          {r.period}: {r.value}
                        </span>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Add'} KPI</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Participants Served" data-testid="kpi-name" /></div>
            <div><Label className="text-xs">Description</Label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} className="w-full border border-[#DED8EB] rounded px-2 py-1 text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Target</Label><Input type="number" value={form.target_value} onChange={e => setForm(f => ({ ...f, target_value: parseFloat(e.target.value) || 0 }))} /></div>
              <div><Label className="text-xs">Unit</Label><Input value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} placeholder="$, %, participants" /></div>
              <div><Label className="text-xs">Frequency</Label>
                <select value={form.frequency} onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))} className="w-full border border-[#DED8EB] rounded px-2 py-1.5 text-sm bg-white">
                  {FREQ.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div><Label className="text-xs">Owner</Label>
                <select value={form.owner_member_id} onChange={e => setForm(f => ({ ...f, owner_member_id: e.target.value }))} className="w-full border border-[#DED8EB] rounded px-2 py-1.5 text-sm bg-white">
                  <option value="">—</option>{members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-[#6B3FA0]" data-testid="save-kpi-btn">{busy ? 'Saving…' : 'Save'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reading} onOpenChange={(v) => !v && setReading(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Add Reading · {reading?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Period *</Label><Input value={readingForm.period} onChange={e => setReadingForm(f => ({ ...f, period: e.target.value }))} placeholder="2026-Q1" data-testid="reading-period" /></div>
            <div><Label className="text-xs">Value *</Label><Input type="number" value={readingForm.value} onChange={e => setReadingForm(f => ({ ...f, value: parseFloat(e.target.value) || 0 }))} data-testid="reading-value" /></div>
            <div><Label className="text-xs">Notes</Label><Input value={readingForm.notes} onChange={e => setReadingForm(f => ({ ...f, notes: e.target.value }))} /></div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setReading(null)}>Cancel</Button>
            <Button onClick={saveReading} disabled={busy} className="bg-[#43A047]" data-testid="save-reading-btn">{busy ? 'Saving…' : 'Add Reading'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
